import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FeatureFlagContextType {
  isEnabled: (flagName: string) => boolean;
  flags: Record<string, boolean>;
  loading: boolean;
  error: string | null;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const url = import.meta.env.VITE_UNLEASH_URL;
        const instanceId = import.meta.env.VITE_UNLEASH_CLIENT_KEY;
        const accessToken = import.meta.env.VITE_GITLAB_ACCESS_TOKEN;
        const appName = import.meta.env.VITE_UNLEASH_APP_NAME || 'tarot-front';

        console.log('🔍 Feature Flags Debug:', {
          url,
          instanceId: instanceId ? 'set' : 'missing',
          accessToken: accessToken ? 'set' : 'missing',
          appName,
        });

        if (!url || !instanceId) {
          console.warn('⚠️ Feature flags not configured. Enabling billing by default.');
          setFlags({ billing: true });
          setLoading(false);
          return;
        }

        const headers: Record<string, string> = {
          'UNLEASH-INSTANCEID': instanceId,
          'UNLEASH-APPNAME': appName,
          'Content-Type': 'application/json',
        };

        // Add GitLab access token if available
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        console.log('📡 Fetching feature flags from GitLab...');
        const response = await fetch(`${url}?appName=${appName}&environment=default`, {
          method: 'GET',
          headers,
        });

        console.log('📥 Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.warn(`⚠️ Feature flags API returned ${response.status}:`, errorText);
          console.log('✅ Using fallback: billing = true');
          setFlags({ billing: true });
          setLoading(false);
          return;
        }

        const data = await response.json();
        console.log('📦 Raw response data:', JSON.stringify(data, null, 2));

        // Parse GitLab Unleash response format
        const parsedFlags: Record<string, boolean> = {};

        // Check different possible response formats
        if (data.toggles && Array.isArray(data.toggles)) {
          console.log('📋 Found toggles array with', data.toggles.length, 'items');
          data.toggles.forEach((toggle: any) => {
            console.log(`🔍 Toggle object:`, JSON.stringify(toggle, null, 2));
            parsedFlags[toggle.name] = toggle.enabled === true;
            console.log(`🚩 Flag "${toggle.name}":`, toggle.enabled);
          });
        } else if (data.features && Array.isArray(data.features)) {
          console.log('📋 Found features array with', data.features.length, 'items');
          data.features.forEach((feature: any) => {
            console.log(`🔍 Feature object:`, JSON.stringify(feature, null, 2));
            parsedFlags[feature.name] = feature.enabled === true;
            console.log(`🚩 Flag "${feature.name}":`, feature.enabled);
          });
        } else {
          console.warn('⚠️ Unexpected response format. Keys:', Object.keys(data));
        }

        console.log('✅ Parsed flags:', parsedFlags);

        // Development override: Check for local override in localStorage
        const localOverride = localStorage.getItem('feature_flags_override');
        if (localOverride) {
          try {
            const overrides = JSON.parse(localOverride);
            console.log('🔧 Applying local overrides:', overrides);
            Object.assign(parsedFlags, overrides);
          } catch (e) {
            console.error('Failed to parse feature flag overrides:', e);
          }
        }

        // If billing flag is not present, add it as true by default in development
        if (!parsedFlags.hasOwnProperty('billing')) {
          console.log('⚠️ Billing flag not found in response, defaulting to true');
          parsedFlags.billing = true;
        }

        setFlags(parsedFlags);
      } catch (err) {
        console.error('❌ Error fetching feature flags:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.log('✅ Using fallback on error: billing = true');
        setFlags({ billing: true });
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();

    // Refresh flags every 30 seconds
    const interval = setInterval(fetchFlags, 30000);

    return () => clearInterval(interval);
  }, []);

  const isEnabled = (flagName: string): boolean => {
    return flags[flagName] === true;
  };

  return (
    <FeatureFlagContext.Provider value={{ isEnabled, flags, loading, error }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
  }
  return context;
};

// Hook to check a specific flag
export const useFlag = (flagName: string): boolean => {
  const { isEnabled } = useFeatureFlag();
  return isEnabled(flagName);
};
